import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import type { InputNumberValueChangeEvent } from "primereact/inputnumber";
import { Message } from "primereact/message";
import type { ToastMessage } from "primereact/toast";

import { requestSeatsAsync } from "@/api/users";

export interface RequestSeatsDialogProps {
  visible: boolean;
  onHide: () => void;
  showToast: (opts: ToastMessage) => void;
  purchasedCount?: number;
  initialSeatCount?: number;
}

export function RequestSeatsDialog({
  visible,
  onHide,
  showToast,
  purchasedCount,
  initialSeatCount,
}: RequestSeatsDialogProps) {
  const [seatCount, setSeatCount] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync the field to the requested count each time the dialog opens (e.g. the number of
  // selected users when routed here from the bulk "Add License" action).
  useEffect(() => {
    if (visible) setSeatCount(initialSeatCount ?? null);
  }, [visible, initialSeatCount]);

  const isValid = typeof seatCount === "number" && Number.isInteger(seatCount) && seatCount >= 1;

  const handleHide = () => {
    setSeatCount(null);
    onHide();
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await requestSeatsAsync(seatCount);
      showToast({
        severity: "success",
        summary: "Request sent",
        detail: "Thanks! Our team will reach out to confirm your purchase.",
      });
      setSeatCount(null);
      onHide();
    } catch {
      showToast({
        severity: "error",
        summary: "Something went wrong",
        detail: "We could not send your request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      header="Request more licenses"
      data-testid="request-seats-popup"
      visible={visible}
      onHide={handleHide}
      style={{ width: "32rem" }}
      blockScroll
      maskStyle={{ backdropFilter: "blur(5px)" }}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            icon="pi pi-times"
            label="Cancel"
            data-testid="cancel-request-seats-button"
            className="p-button-outlined p-button-danger"
            onClick={handleHide}
          />
          <Button
            type="button"
            icon="pi pi-send"
            label="Send request"
            data-testid="submit-request-seats-button"
            disabled={!isValid}
            loading={isSubmitting}
            onClick={() => void handleSubmit()}
          />
        </div>
      }
    >
      <div className="mt-2 flex flex-col gap-4">
        <p className="m-0">
          You have reached your purchased license limit
          {typeof purchasedCount === "number" ? ` of ${purchasedCount}` : ""}. To add more users,
          request additional licenses below.
        </p>

        <div className="flex flex-col gap-2">
          <label htmlFor="seat-count" className="font-semibold">
            How many additional licenses would you like to purchase?
          </label>
          <InputNumber
            inputId="seat-count"
            data-testid="seat-count-input"
            value={seatCount}
            onValueChange={(e: InputNumberValueChangeEvent) => setSeatCount(e.value ?? null)}
            min={1}
            step={1}
            minFractionDigits={0}
            maxFractionDigits={0}
            useGrouping={false}
            showButtons
            placeholder="Enter a number"
          />
        </div>

        <Message severity="info" text="We will reach out to confirm your purchase." />
      </div>
    </Dialog>
  );
}
